import * as React from 'react';
import type { SVGProps } from 'react';
import OtIcon from './OtIcon';

/**
 * Illustrated family — business and document group.
 * Lighting model and construction rules live in {@link OtIcon}.
 */

type TProps = SVGProps<SVGSVGElement>;

const Spec = ({ d, o = 0.92, w = 2.6 }: { d: string; o?: number; w?: number }) => (
	<path d={d} stroke='#fff' strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

const Bounce = ({ d, c, o = 0.5, w = 2 }: { d: string; c: string; o?: number; w?: number }) => (
	<path d={d} stroke={c} strokeOpacity={o} strokeWidth={w} strokeLinecap='round' fill='none' />
);

/** A sheet of paper, shared by every document-shaped icon so they read as one object type. */
const Sheet = ({ fill, rim, occ }: { fill: string; rim: string; occ: string }) => (
	<>
		<path d='M22 14h30l18 18v42a8 8 0 0 1-8 8H22a8 8 0 0 1-8-8V22a8 8 0 0 1 8-8z' fill={fill} />
		<path
			d='M22 14h30l18 18v42a8 8 0 0 1-8 8H22a8 8 0 0 1-8-8V22a8 8 0 0 1 8-8z'
			fill='none'
			stroke={rim}
			strokeWidth='3.2'
			strokeLinejoin='round'
		/>
		{/* the folded corner: a lit triangle over a shadow, which is what gives the sheet thickness */}
		<path d='M52 14l18 18H52z' fill={occ} fillOpacity='.5' />
		<path d='M52 14l18 18H56a4 4 0 0 1-4-4z' fill='#fff' fillOpacity='.5' />
	</>
);

/* ------------------------------------------------------------------ Business */
export const OtBusiness = (props: TProps) => (
	<OtIcon slug='business' palette='indigo' groundX={46} groundY={80} groundR={26} {...props}>
		{(r) => (
			<>
				{/* back tower, in shadow */}
				<rect x='50' y='26' width='32' height='48' rx='7' fill={r.fill} opacity='.62' />
				<rect
					x='50'
					y='26'
					width='32'
					height='48'
					rx='7'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.6'
				/>
				<ellipse
					cx='52'
					cy='50'
					rx='6'
					ry='24'
					fill={r.tone.occlusion}
					opacity='.4'
					filter={r.tight}
				/>
				{/* front tower */}
				<rect x='12' y='14' width='40' height='60' rx='8' fill={r.fill} />
				<rect
					x='12'
					y='14'
					width='40'
					height='60'
					rx='8'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				<rect x='21' y='24' width='9' height='9' rx='3' fill='#fff' fillOpacity='.9' />
				<rect x='35' y='24' width='9' height='9' rx='3' fill='#fff' fillOpacity='.7' />
				<rect x='21' y='39' width='9' height='9' rx='3' fill='#fff' fillOpacity='.7' />
				<rect x='35' y='39' width='9' height='9' rx='3' fill='#fff' fillOpacity='.55' />
				<rect x='27' y='56' width='12' height='18' rx='5' fill={r.body} fillOpacity='.85' />
				<rect x='60' y='36' width='9' height='9' rx='3' fill='#fff' fillOpacity='.45' />
				<rect x='60' y='51' width='9' height='9' rx='3' fill='#fff' fillOpacity='.35' />
				<Spec d='M18 24a8 8 0 0 1 8-6.6h10' w={2.4} />
				<Bounce d='M78 62a7 7 0 0 1-6 6' c='#7fe6d2' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ CardMembership */
export const OtCardMembership = (props: TProps) => (
	<OtIcon slug='card-membership' palette='pink' groundX={48} groundY={80} groundR={24} {...props}>
		{(r) => (
			<>
				<rect x='10' y='16' width='76' height='42' rx='11' fill={r.fill} />
				<rect
					x='10'
					y='16'
					width='76'
					height='42'
					rx='11'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				<rect x='10' y='26' width='76' height='8' fill={r.tone.occlusion} fillOpacity='.5' />
				<rect x='20' y='42' width='26' height='6' rx='3' fill='#fff' fillOpacity='.85' />
				<rect x='52' y='42' width='14' height='6' rx='3' fill='#fff' fillOpacity='.5' />
				{/* ribbon tails, hanging below the card and catching the bounce */}
				<path d='M36 58v22l10-7 10 7V58z' fill={r.fill} />
				<path
					d='M36 58v22l10-7 10 7V58z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3'
					strokeLinejoin='round'
				/>
				<path d='M46 73v7' stroke={r.tone.occlusion} strokeOpacity='.5' strokeWidth='2' />
				<Spec d='M17 26a10 10 0 0 1 9-7h14' w={2.4} />
				<Bounce d='M80 46a10 10 0 0 1-6 8' c='#7fe6d2' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Description */
export const OtDescription = (props: TProps) => (
	<OtIcon slug='description' palette='blue' groundX={42} groundY={83} groundR={22} {...props}>
		{(r) => (
			<>
				<Sheet fill={r.fill} rim={r.rim} occ={r.tone.occlusion} />
				<rect x='24' y='42' width='36' height='5' rx='2.5' fill='#fff' fillOpacity='.9' />
				<rect x='24' y='53' width='36' height='5' rx='2.5' fill='#fff' fillOpacity='.7' />
				<rect x='24' y='64' width='22' height='5' rx='2.5' fill='#fff' fillOpacity='.5' />
				<Spec d='M20 24a8 8 0 0 1 8-6.6h12' w={2.4} />
				<Bounce d='M66 66a8 8 0 0 1-6 6' c='#7fe6d2' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Assignment */
export const OtAssignment = (props: TProps) => (
	<OtIcon slug='assignment' palette='indigo' groundX={46} groundY={82} groundR={23} {...props}>
		{(r) => (
			<>
				<rect x='14' y='18' width='64' height='58' rx='11' fill={r.fill} />
				<rect
					x='14'
					y='18'
					width='64'
					height='58'
					rx='11'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				<rect
					x='16.7'
					y='20.7'
					width='58.6'
					height='52.6'
					rx='8.8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.4'
				/>
				<rect x='26' y='40' width='40' height='5' rx='2.5' fill='#fff' fillOpacity='.85' />
				<rect x='26' y='51' width='40' height='5' rx='2.5' fill='#fff' fillOpacity='.65' />
				<rect x='26' y='62' width='26' height='5' rx='2.5' fill='#fff' fillOpacity='.45' />
				{/* the clip sits above the board and casts onto it */}
				<ellipse cx='46' cy='19' rx='15' ry='3.4' fill={r.tone.occlusion} opacity='.5' />
				<rect x='32' y='9' width='28' height='17' rx='6.5' fill={r.body} />
				<rect
					x='32'
					y='9'
					width='28'
					height='17'
					rx='6.5'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.4'
				/>
				<Spec d='M21 28a9 9 0 0 1 9-7.4h10' w={2.4} />
				<Bounce d='M73 64a9 9 0 0 1-6 6.6' c='#7fe6d2' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ AssignmentTurnedIn */
export const OtAssignmentTurnedIn = (props: TProps) => (
	<OtIcon
		slug='assignment-turned-in'
		palette='green'
		groundX={46}
		groundY={82}
		groundR={23}
		{...props}>
		{(r) => (
			<>
				<rect x='14' y='18' width='64' height='58' rx='11' fill={r.fill} />
				<rect
					x='14'
					y='18'
					width='64'
					height='58'
					rx='11'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
				/>
				<rect
					x='16.7'
					y='20.7'
					width='58.6'
					height='52.6'
					rx='8.8'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.4'
				/>
				{/* the tick is engraved: a shadow stroke under a lit one */}
				<path
					d='M31 50l11 11 22-22'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='9'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<path
					d='M30.2 49.2l11 11 22-22'
					fill='none'
					stroke='#fff'
					strokeWidth='6'
					strokeLinecap='round'
					strokeLinejoin='round'
				/>
				<ellipse cx='46' cy='19' rx='15' ry='3.4' fill={r.tone.occlusion} opacity='.5' />
				<rect x='32' y='9' width='28' height='17' rx='6.5' fill={r.body} />
				<rect
					x='32'
					y='9'
					width='28'
					height='17'
					rx='6.5'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.4'
				/>
				<Spec d='M21 28a9 9 0 0 1 9-7.4h10' w={2.4} />
				<Bounce d='M73 64a9 9 0 0 1-6 6.6' c={r.tone.bounce} o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Receipt */
export const OtReceipt = (props: TProps) => (
	<OtIcon slug='receipt' palette='green' groundX={46} groundY={83} groundR={20} {...props}>
		{(r) => (
			<>
				{/* torn bottom edge: the tell that says receipt and not document */}
				<path
					d='M18 14h56v62l-8-6-8 6-8-6-8 6-8-6-8 6-8-6z'
					fill={r.fill}
				/>
				<path
					d='M18 14h56v62l-8-6-8 6-8-6-8 6-8-6-8 6-8-6z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<rect x='27' y='28' width='38' height='5' rx='2.5' fill='#fff' fillOpacity='.88' />
				<rect x='27' y='39' width='30' height='5' rx='2.5' fill='#fff' fillOpacity='.66' />
				<rect x='27' y='50' width='38' height='5' rx='2.5' fill='#fff' fillOpacity='.5' />
				<Spec d='M24 24a6 6 0 0 1 6-5h12' w={2.4} />
				<Bounce d='M70 52v14' c={r.tone.bounce} o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ RequestQuote */
export const OtRequestQuote = (props: TProps) => (
	<OtIcon slug='request-quote' palette='gold' groundX={42} groundY={83} groundR={22} {...props}>
		{(r) => (
			<>
				<Sheet fill={r.fill} rim={r.rim} occ={r.tone.occlusion} />
				{/* the figure is embossed into the sheet */}
				<path
					d='M50 44h-9a6 6 0 0 0 0 12h6a6 6 0 0 1 0 12h-9M45.5 40v4M45.5 68v4'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='7'
					strokeLinecap='round'
				/>
				<path
					d='M49.2 43.2h-9a6 6 0 0 0 0 12h6a6 6 0 0 1 0 12h-9M44.7 39.2v4M44.7 67.2v4'
					fill='none'
					stroke='#fff'
					strokeWidth='4.2'
					strokeLinecap='round'
				/>
				<Spec d='M20 24a8 8 0 0 1 8-6.6h12' w={2.4} />
				<Bounce d='M66 66a8 8 0 0 1-6 6' c='#ffd7a0' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Gavel */
export const OtGavel = (props: TProps) => (
	<OtIcon slug='gavel' palette='gold' groundX={44} groundY={82} groundR={26} {...props}>
		{(r) => (
			<>
				{/* block */}
				<rect x='12' y='64' width='58' height='12' rx='6' fill={r.fill} />
				<rect
					x='12'
					y='64'
					width='58'
					height='12'
					rx='6'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.8'
				/>
				{/* handle */}
				<path
					d='M40 32l30 30'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='12'
					strokeLinecap='round'
				/>
				<path d='M39 31l30 30' stroke={r.fill} strokeWidth='9' strokeLinecap='round' />
				<path
					d='M37.6 29.6l30 30'
					stroke='#fff'
					strokeOpacity='.6'
					strokeWidth='2.6'
					strokeLinecap='round'
				/>
				{/* head */}
				<rect
					x='12'
					y='16'
					width='34'
					height='18'
					rx='7'
					transform='rotate(-45 29 25)'
					fill={r.fill}
				/>
				<rect
					x='12'
					y='16'
					width='34'
					height='18'
					rx='7'
					transform='rotate(-45 29 25)'
					fill='none'
					stroke={r.rim}
					strokeWidth='3'
				/>
				<Spec d='M17 22a7 7 0 0 1 6-5.4' o={0.9} />
				<Bounce d='M62 68h8' c='#ffd7a0' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Flag */
export const OtFlag = (props: TProps) => (
	<OtIcon slug='flag' palette='red' groundX={26} groundY={80} groundR={13} {...props}>
		{(r) => (
			<>
				{/* pole, as a cylinder */}
				<path d='M23 12v64' stroke={r.tone.occlusion} strokeOpacity='.6' strokeWidth='9' strokeLinecap='round' />
				<path d='M22 12v64' stroke={r.body} strokeWidth='5.4' strokeLinecap='round' />
				{/* the cloth waves, so the trough is in shadow and the crest catches the key light */}
				<path
					d='M26 16c12-6 22 6 34 0 6-3 10-3 14-1v34c-4-2-8-2-14 1-12 6-22-6-34 0z'
					fill={r.fill}
				/>
				<path
					d='M26 16c12-6 22 6 34 0 6-3 10-3 14-1v34c-4-2-8-2-14 1-12 6-22-6-34 0z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.2'
					strokeLinejoin='round'
				/>
				<path
					d='M43 21c6 3 11 2 17-1'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.35'
					strokeWidth='3'
					strokeLinecap='round'
				/>
				<Spec d='M29 17a30 30 0 0 1 10-2.6' o={0.75} w={2.4} />
				<Bounce d='M71 44a20 20 0 0 1-8 3' c='#ffb3d8' o={0.45} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ EmojiEvents */
export const OtEmojiEvents = (props: TProps) => (
	<OtIcon slug='emoji-events' palette='gold' groundX={46} groundY={81} groundR={20} {...props}>
		{(r) => (
			<>
				{/* handles, behind the cup */}
				<path
					d='M22 22h-8a10 10 0 0 0 0 20h10M70 22h8a10 10 0 0 1 0 20H78'
					fill='none'
					stroke={r.rim}
					strokeWidth='5'
					strokeLinecap='round'
				/>
				<path d='M22 14h48v20c0 14-11 24-24 24S22 48 22 34z' fill={r.fill} />
				<path
					d='M22 14h48v20c0 14-11 24-24 24S22 48 22 34z'
					fill='none'
					stroke={r.rim}
					strokeWidth='3.4'
					strokeLinejoin='round'
				/>
				{/* stem and base */}
				<rect x='40' y='57' width='12' height='10' fill={r.fill} />
				<rect x='40' y='57' width='12' height='10' fill='none' stroke={r.rim} strokeWidth='2.4' />
				<rect x='26' y='66' width='40' height='10' rx='5' fill={r.fill} />
				<rect
					x='26'
					y='66'
					width='40'
					height='10'
					rx='5'
					fill='none'
					stroke={r.rim}
					strokeWidth='2.8'
				/>
				<ellipse cx='46' cy='67' rx='9' ry='2.4' fill={r.tone.occlusion} opacity='.45' />
				<Spec d='M29 20a7 7 0 0 1 6-4.6' o={0.95} w={3} />
				<Bounce d='M62 44a20 20 0 0 1-7 9' c='#ffd7a0' o={0.5} />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Schedule */
export const OtSchedule = (props: TProps) => (
	<OtIcon slug='schedule' palette='blue' groundX={46} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.fill} />
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.4' />
				<circle
					cx='46'
					cy='42'
					r='29.5'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.4'
					strokeWidth='1.6'
				/>
				<circle cx='46' cy='14' r='2.4' fill='#fff' fillOpacity='.8' />
				<circle cx='74' cy='42' r='2.4' fill='#fff' fillOpacity='.55' />
				<circle cx='46' cy='70' r='2.4' fill='#fff' fillOpacity='.45' />
				<circle cx='18' cy='42' r='2.4' fill='#fff' fillOpacity='.7' />
				{/* hands, engraved */}
				<path
					d='M46 42V24M46 42l14 9'
					stroke={r.tone.occlusion}
					strokeOpacity='.5'
					strokeWidth='7'
					strokeLinecap='round'
				/>
				<path
					d='M45.2 41.2V23.2M45.2 41.2l14 9'
					stroke='#fff'
					strokeWidth='4.4'
					strokeLinecap='round'
				/>
				<circle cx='46' cy='42' r='4' fill={r.body} />
				<Spec d='M27 28a30 30 0 0 1 13-9' />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c='#7fe6d2' />
			</>
		)}
	</OtIcon>
);

/* ------------------------------------------------------------------ Block */
export const OtBlock = (props: TProps) => (
	<OtIcon slug='block' palette='red' groundX={46} groundY={80} groundR={22} {...props}>
		{(r) => (
			<>
				<circle cx='46' cy='42' r='32' fill={r.fill} />
				<circle cx='46' cy='42' r='32' fill='none' stroke={r.rim} strokeWidth='3.6' />
				<circle
					cx='46'
					cy='42'
					r='29.2'
					fill='none'
					stroke={r.tone.occlusion}
					strokeOpacity='.42'
					strokeWidth='1.6'
				/>
				{/* the bar is cut through the disc, so it carries the disc's own shadow under it */}
				<path
					d='M25 63L67 21'
					stroke={r.tone.occlusion}
					strokeOpacity='.55'
					strokeWidth='11'
					strokeLinecap='round'
				/>
				<path d='M24 62L66 20' stroke='#fff' strokeWidth='7.5' strokeLinecap='round' />
				<Spec d='M27 28a30 30 0 0 1 13-9' o={0.7} />
				<Bounce d='M67 60a30 30 0 0 1-11 8' c='#ffb3d8' o={0.45} />
			</>
		)}
	</OtIcon>
);
